// The handover rules of plan §6, independent of any UI so they are tested once and shared by
// the kiosk, the teacher app's roaming handover and the iPad shell.
import { checkPin, codeHash, normaliseCode, uuidv7, verifyPresented, type AuthMethod, type Flag, type PreImage, type Stroke } from "@vavr/core";
import type { Roster } from "./roster.ts";

export interface CollectingAdult {
  id: string; first: string; last: string; photo_sha256: string | null; pin_hash: string | null;
  relation: string; kind: "guardian" | "authorised" | "delegate"; delegation_id: string | null;
}

/** Adults allowed to collect `childId` at `now`: guardians, authorised adults in their window, live delegations. */
export function allowedAdults(r: Roster, childId: string, now = Date.now()): CollectingAdult[] {
  const byId = new Map(r.adults.map((a) => [a.id, a]));
  const out = new Map<string, CollectingAdult>();
  for (const l of r.links) {
    if (l.child_id !== childId) continue;
    if ((l.valid_from != null && l.valid_from > now) || (l.valid_to != null && l.valid_to < now)) continue;
    const a = byId.get(l.adult_id);
    if (a) out.set(a.id, { ...a, relation: l.relation, kind: l.kind, delegation_id: null });
  }
  for (const d of r.delegations) {
    if (!d.child_ids.includes(childId) || d.valid_from > now || d.valid_to < now) continue;
    const a = byId.get(d.delegate_adult_id);
    if (a && !out.has(a.id)) out.set(a.id, { ...a, relation: "delegate", kind: "delegate", delegation_id: d.id });
  }
  // Restricted adults are never offered as a tile, even if also linked.
  const barred = new Set(restrictionsFor(r, childId, now).map((x) => x.subject_adult_id).filter(Boolean));
  return [...out.values()].filter((a) => !barred.has(a.id)).sort((a, b) => kindOrder(a.kind) - kindOrder(b.kind) || a.first.localeCompare(b.first));
}
const kindOrder = (k: string) => (k === "guardian" ? 0 : k === "authorised" ? 1 : 2);

export function restrictionsFor(r: Roster, childId: string, now = Date.now()) {
  return r.restrictions.filter((x) => x.child_id === childId && (x.valid_to == null || x.valid_to >= now));
}

export function siblings(r: Roster, childId: string, now = Date.now()): Roster["children"] {
  const guardians = new Set(r.links.filter((l) => l.child_id === childId && l.kind === "guardian").map((l) => l.adult_id));
  const ids = new Set(r.links.filter((l) => guardians.has(l.adult_id) && l.kind === "guardian" && l.child_id !== childId).map((l) => l.child_id));
  void now;
  return r.children.filter((c) => ids.has(c.id));
}

/** Search by first letters of first or last name ("ma", "lop", "ma lo"). */
export function searchChildren(r: Roster, q: string, roomId?: string | null) {
  const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return r.children.filter((c) => (!roomId || c.room_id === roomId)
    && terms.every((t) => c.first.toLowerCase().startsWith(t) || c.last.toLowerCase().startsWith(t)));
}

/** Checks a delegate's one-time code offline against the mirror. */
export async function matchDelegationCode(r: Roster, childId: string, code: string, now = Date.now()) {
  const norm = normaliseCode(code);
  for (const d of r.delegations) {
    if (!d.child_ids.includes(childId) || d.valid_from > now || d.valid_to < now || !d.code_hash) continue;
    if ((await codeHash(d.id, norm)) === d.code_hash) return d;
  }
  return null;
}

/** Checks a scanned guardian or delegate QR offline with the site's public key. */
export async function checkQr(r: Roster, qr: string, sitePubkey: string, now = Date.now()) {
  const res = await verifyPresented(qr, sitePubkey, r.site_id, Math.floor(now / 1000));
  if (!res.ok) return res;
  const childIds = res.pass.child_ids.filter((id) => r.children.some((c) => c.id === id));
  return { ...res, childIds };
}

// ---- staff PIN unlock, with lockout ----
export class StaffLock {
  failures = 0;
  lockedUntil = 0;
  maxFailures = 5;
  lockMs = 5 * 60_000;
  async unlock(r: Roster, pin: string, now = Date.now()) {
    if (now < this.lockedUntil) return { ok: false as const, reason: "locked" as const, until: this.lockedUntil };
    for (const s of r.staff) {
      if (await checkPin(s.id, pin, s.pin_hash)) { this.failures = 0; return { ok: true as const, staff: s }; }
    }
    this.failures++;
    if (this.failures >= this.maxFailures) { this.lockedUntil = now + this.lockMs; this.failures = 0; }
    return { ok: false as const, reason: "wrong" as const, until: this.lockedUntil };
  }
}

// ---- building the event ----
export interface HandoverInput {
  siteId: string;
  deviceId: string;
  /** null on a pickup_request from the self-service kiosk. */
  staffId: string | null;
  type: "pickup" | "dropoff" | "pickup_request" | "dropoff_request";
  childId: string;
  groupId: string | null;
  adult: { id: string; delegationId?: string | null } | null; // null = someone else
  claimedName?: string | null;
  idCheck?: PreImage["id_check"];
  witnessStaffId?: string | null;
  authMethod: AuthMethod;
  photoSha256: string | null;
  face: PreImage["face"];
  strokeSha256: string | null;
  cannotSign?: boolean;
  typedName?: boolean;
  restrictionAck?: boolean;
  /** Self-service request with no PIN, pass or code: staff check the adult in person. */
  checkId?: boolean;
  offline?: boolean;
  attestation: string;
  monoStart: number; // performance.timeOrigin-style start, for device_mono
  now?: number;
}

export function buildPreImage(i: HandoverInput): PreImage {
  const now = i.now ?? Date.now();
  const flags: Flag[] = [];
  if (i.cannotSign) flags.push("cannot_sign");
  if (i.checkId) flags.push("check_id");
  if (i.offline) flags.push("offline");
  if (i.restrictionAck) flags.push("restriction_ack");
  if (i.typedName) flags.push("typed_name");
  if (!i.adult) flags.push("unlisted_adult");
  flags.sort();
  return {
    v: 1, id: uuidv7(now), site_id: i.siteId, type: i.type, group_id: i.groupId, child_id: i.childId,
    adult_id: i.adult?.id ?? null, claimed_name: i.adult ? null : (i.claimedName ?? null), id_check: i.idCheck ?? null,
    staff_id: i.staffId, witness_staff_id: i.witnessStaffId ?? null, device_id: i.deviceId, device_ts: now,
    device_mono: Math.max(0, Math.round(now - i.monoStart)), photo_sha256: i.photoSha256, face: i.face, stroke_sha256: i.strokeSha256,
    attestation: i.attestation.normalize("NFC"), auth_method: i.authMethod, flags, reason: null,
    data: i.adult?.delegationId ? { delegation_id: i.adult.delegationId } : null,
  };
}

// ---- signature strokes ----
/** Collects pointer samples into the canonical integer stroke format (docs/spec/events.md §4). */
export class StrokeRecorder {
  w: number; h: number;
  strokes: [number, number, number][][] = [];
  private t0: number | null = null;
  constructor(w: number, h: number) { this.w = Math.round(w); this.h = Math.round(h); }
  start(x: number, y: number, t: number) { this.t0 ??= t; this.strokes.push([[Math.round(x), Math.round(y), Math.round(t - this.t0)]]); }
  move(x: number, y: number, t: number) {
    const line = this.strokes.at(-1);
    if (!line || this.t0 == null) return;
    const p: [number, number, number] = [Math.round(x), Math.round(y), Math.round(t - this.t0)];
    const last = line.at(-1)!;
    if (p[0] !== last[0] || p[1] !== last[1]) line.push(p);
  }
  clear() { this.strokes = []; this.t0 = null; }
  points() { return this.strokes.reduce((n, l) => n + l.length, 0); }
  /** Plan §6: no shape validation beyond about 20 points. */
  isEnough() { return this.points() >= 20; }
  toStroke(): Stroke { return { v: 1, w: this.w, h: this.h, strokes: this.strokes.map((l) => l.map((p) => [...p] as [number, number, number])) }; }
}
